<template>
  <section v-if="!loading" class="login">
    <start-logo></start-logo>
    <div v-if="isForbidden" class="mt-5">
      <div class="alert alert-light">{{ $t('src/pages/registration.7885', { '1': authUserName }) }}</div>
      <div class="d-flex justify-content-around">
        <button type="button" class="btn btn-dark" @click="goHome">
          {{ $t('button.home') }}
        </button>
        <button type="button" class="btn btn-secondary" @click.prevent="logout">
          {{ $t('src/pages/registration.1962') }}
        </button>
      </div>
    </div>
    <template v-else>
      <div v-if="loading">
        <base-spinner></base-spinner>
      </div>
      <div v-else-if="!user.login">
        <div class="alert alert-light text-center mt-4">{{ $t('src/pages/registration.7450') }}</div>
        <button class="btn btn-dark btn-block" type="button" @click="goLoginPage">
          {{ $t('src/pages/registration.login') }}
        </button>
      </div>
      <form v-else class="form-signin" @submit.prevent="confirm">
        <h5 class="text-white">{{ $t('src/pages/registration.1702') }}</h5>
        <div class="pb-2">
          <div class="form-group input-group">
            <div class="input-group-prepend">
              <span class="input-group-text icon icon-login"></span>
            </div>
            <label class="sr-only">{{ $t('general.login') }}</label>
            <input
              type="text"
              name="login"
              class="form-control"
              :class="{ 'is-invalid': $v.user.login.$invalid }"
              v-model="user.login"
              @blur="$v.user.login.$touch()"
            />
          </div>
          <div class="form-group input-group">
            <div class="input-group-prepend">
              <span class="input-group-text icon icon-password"></span>
            </div>
            <label class="sr-only">{{ $t('general.pass') }}</label>
            <input
              type="password"
              name="password"
              class="form-control"
              :class="{ 'is-invalid': $v.user.password.$error }"
              v-model.trim="user.password"
              @blur="$v.user.password.$touch()"
              :placeholder="$t('src/pages/registration.9689')"
              ref="password"
            />
          </div>
          <div class="form-group input-group">
            <div class="input-group-prepend">
              <span class="input-group-text icon icon-password"></span>
            </div>
            <label class="sr-only">{{ $t('src/pages/registration.5167') }}</label>
            <input
              type="password"
              name="confirm_password"
              class="form-control"
              :class="{ 'is-invalid': $v.user.confirmPass.$error }"
              v-model="user.confirmPass"
              @blur="$v.user.confirmPass.$touch()"
              :placeholder="$t('src/pages/registration.5167')"
            />
          </div>
        </div>
        <div class="mt-2 mb-2">
          <div class="form-control-error" v-if="!$v.user.login.required && $v.user.login.$error">
            {{ $t('src/pages/login.1971') }}
          </div>
          <div class="form-control-error" v-if="!$v.user.login.notIn">
            {{ $t('src/pages/registration.2195') }}
          </div>
          <div class="form-control-error" v-show="!$v.user.password.required && $v.user.password.$error">
            {{ $t('src/pages/login.5222') }}
          </div>
          <div class="form-control-error" v-show="!$v.user.password.safePass && $v.user.password.$error">
            {{ $t('src/pages/registration.4613') }}
          </div>
          <div class="form-control-error" v-show="!$v.user.confirmPass.sameAs && $v.user.confirmPass.$error">
            {{ $t('src/pages/registration.321') }}
          </div>
        </div>
        <base-checkbox name="checkbox" v-model="checked" class="text-white">{{
          $t('src/pages/login.agreement')
        }}</base-checkbox>
        <div class="pt-2">
          <button type="submit" class="btn btn-lg btn-dark btn-block" :disabled="!checked">
            <span v-if="saving" class="spinner-border spinner-border-sm"></span
            >{{ $t('src/pages/registration.2754') }}
          </button>
        </div>
      </form>
    </template>
  </section>
</template>

<script>
import http from '@/modules/http.js'
import { required, sameAs } from 'vuelidate/lib/validators'
import { notIn, safePass } from '@/modules/validators'
import StartLogo from '@/views/StartLogo.vue'
import('@/styles/components/login.less')

export default {
  name: 'RegistrationPage',
  components: {
    StartLogo,
    BaseSpinner: () => import('@/base/BaseSpinner.vue'),
    BaseCheckbox: () => import('@/base/BaseCheckbox.vue')
  },
  data: () => ({
    user: {
      password: '',
      confirmPass: ''
    },
    loading: true,
    excludedLogins: [],
    saving: false,
    checked: false
  }),
  validations() {
    return {
      user: {
        login: {
          required,
          notIn: value => notIn(value, this.excludedLogins)
        },
        password: {
          required,
          safePass
        },
        confirmPass: {
          required,
          sameAs: sameAs('password')
        }
      }
    }
  },
  computed: {
    isForbidden() {
      return this.$store.getters.HAVE_AUTH_USER
    },
    authUserName() {
      return this.$store.getters.AUTH_USER.login
    }
  },
  mounted() {
    this.getUserByToken()
  },
  methods: {
    logout() {
      this.$store.dispatch('AUTH_LOGOUT').then(() => {
        this.getUserByToken()
      })
    },
    goHome() {
      this.$router.push('/')
    },
    getUserByToken() {
      const token = this.$route.params.token

      http
        .get('/get-userid-by-regtoken/' + token)
        .then(response => {
          this.user = response.data
          this.user.regtoken = token
          this.resetUserPasswordById(this.user.id)
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    confirm() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.$emit('submit')
        this.$v.$reset()

        this.saving = true
        http
          .put('/users/' + this.user.id, this.user)
          .then(() => {
            this.$router.push({ name: 'login', params: { login: this.user.login } })
          })
          .catch(error => {
            if (error.response.status === 409) {
              this.excludedLogins.push(this.user.login)
            } else {
              console.log(error)
              alert(this.$t('errorMessage.wrong'))
            }
          })
          .finally(() => {
            this.saving = false
          })
      }
    },
    resetUserPasswordById(userId) {
      this.$store.dispatch('RESET_PASSWORD', userId)
    },
    goLoginPage() {
      this.$router.push('/login')
    }
  }
}
</script>
